import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import LoadingButton from "@shared/components/LoadingButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { companiesService } from "@shared/services/companiesService";
import { structuralGroupService } from "../services/structuralGroups";
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import CustomDatePicker from "@shared/components/CustomDatePicker";

export default function ChangeConstCenter({ onClose, companyId, editData }) {
    const modalRef = useRef(null);
    const { t } = useTranslation();
    const [showAddRow, setShowAddRow] = useState(false);
    const [saving, setSaving] = useState(false);
    const [companyCentersLoading, setCompanyCentersLoading] = useState(true);
    const [companyCentersData, setCompanyCentersData] = useState([]);
    const [positionsCostCenterData, setPositionsCostCenterData] = useState([]);
    const [positionsCostCenterLoading, setPositionsCostCenterLoading] = useState(true);

    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [costCenter, setCostCenter] = useState('');

    const [tableData, setTableData] = useState([]);

    // Helper function to format dates in Bulgarian format
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}.${month}.${year}`;
        }
        return dateString;
    };

    useEffect(() => {
        const fetchCompanyCenters = async () => {
            if (!companyId) {
                return;
            }

            try {
                setCompanyCentersLoading(true);
                const response = await companiesService.getCompanyCenters();

                const centersArray = Array.isArray(response) ? response : (response.data || []);
                setCompanyCentersData(centersArray);
            } catch (error) {
                console.error('Error fetching company centers:', error);
                setCompanyCentersData([]);
            } finally {
                setCompanyCentersLoading(false);
            }
        };

        fetchCompanyCenters();
    }, []);

    useEffect(() => {
        const fetchPositionsCostCenter = async () => {
            if (!editData?.id) {
                return;
            }

            try {
                setPositionsCostCenterLoading(true);
                const response = await structuralGroupService.getPositionsCostCenter(editData.id);

                const transformedData = response.map(item => ({
                    id: item.id?.id || item.id,
                    fromDate: item.id?.fromDate ? new Date(item.id.fromDate).toISOString().split('T')[0] : '',
                    toDate: item.toDate ? new Date(item.toDate).toISOString().split('T')[0] : '',
                    costCenter: item.empOffices?.nane || item.empOffices?.name || 'N/A',
                    costCenterId: item.empOffices?.officeId || item.empOffices?.id,
                    originalData: item
                }));


                setPositionsCostCenterData(response);
                setTableData(transformedData);
            } catch (error) {
                console.error('Error fetching positions cost center:', error);
                setPositionsCostCenterData([]);
                setTableData([]);
            } finally {
                setPositionsCostCenterLoading(false);
            }
        };

        fetchPositionsCostCenter();
    }, [editData]);


    const handleSave = async () => {
        if (!fromDate || !costCenter) {
            return;
        }

        try {
            setSaving(true);

            const requestData = {
                hrPositionId: editData.id,
                empOfficesId: costCenter,
                fromDate: fromDate ? fromDate.toISOString().split('T')[0] : '',
                toDate: '3000-01-01'
            };


            await structuralGroupService.changePositionsCostCenter(requestData);

            const response = await structuralGroupService.getPositionsCostCenter(editData.id);

            const transformedData = response.map(item => ({
                id: item.id?.id || item.id,
                fromDate: item.id?.fromDate ? new Date(item.id.fromDate).toISOString().split('T')[0] : '',
                toDate: item.toDate ? new Date(item.toDate).toISOString().split('T')[0] : '',
                costCenter: item.empOffices?.nane || item.empOffices?.name || 'N/A',
                costCenterId: item.empOffices?.officeId || item.empOffices?.id,
                originalData: item
            }));
            notifySuccess(t('common.createdSuccessfully'));
            setTableData(transformedData);

            setShowAddRow(false);
            setFromDate('');
            setToDate('');
            setCostCenter('');
        } catch (error) {
            console.error('Error saving:', error);
            notifyError(t('common.errorMessage'));
        } finally {
            setSaving(false);
        }
    };



    const handleAddClick = () => {
        setShowAddRow(true);
    };

    const actionButtons = (
        <button
            className="icon-button"
            onClick={handleAddClick}
            title={t('common.add')}
        >
            <FontAwesomeIcon icon={faPlus} />
        </button>
    );

    return (
        <div className="modal show" tabIndex="-1">
            <div ref={modalRef} className="form-wrapper top-20 width-60">
                <div className="form">
                    <h2 className="title">{t("common.edit")} {t('organizationalStructure.structuralGroups.form.costCenter')}</h2>
                    <div className="flex-row justify-content-center">
                        {actionButtons}
                    </div>
                    <div className="table-responsive custom-table-container">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>{t('organizationalStructure.levelsOfStructuralGroups.form.fromDate')}</th>
                                    <th>{t('organizationalStructure.levelsOfStructuralGroups.form.toDate')}</th>
                                    <th>{t('organizationalStructure.structuralGroups.form.costCenter')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {showAddRow && (
                                    <tr className="add-row">
                                        <td>
                                            <CustomDatePicker
                                                name="fromDate"
                                                value={fromDate}
                                                onChange={setFromDate}
                                                isSmall={true}
                                                placeholderText={t('organizationalStructure.levelsOfStructuralGroups.form.fromDate')}
                                            />
                                        </td>
                                        <td>
                                   
                                        </td>
                                        <td>
                                            <select
                                                className="form-control"
                                                value={costCenter || ''}
                                                onChange={(e) => {
                                                    setCostCenter(e.target.value);
                                                }}
                                            >
                                                <option value="">{t('common.select')}</option>
                                                {companyCentersLoading ? (
                                                    <option value="">Loading company centers...</option>
                                                ) : (!Array.isArray(companyCentersData) || companyCentersData.length === 0) ? (
                                                    <option value="">No company centers found</option>
                                                ) : (
                                                    companyCentersData.map((companyCenter) => (
                                                        <option key={companyCenter.officeId} value={companyCenter.officeId}>
                                                            {companyCenter.officeNo} - {companyCenter.name}
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                        </td>
                                    </tr>
                                )}
                                {positionsCostCenterLoading ? (
                                    <tr>
                                        <td colSpan="4" className="text-center">Loading cost center data...</td>
                                    </tr>
                                ) : tableData.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="text-center">No cost center data available</td>
                                    </tr>
                                ) : (
                                    <>
                                        {tableData.map((item) => (
                                            <tr key={item.id}>
                                                <td>{formatDate(item.fromDate)}</td>
                                                <td>{item.toDate === '3000-01-01' ? 'Permanent' : formatDate(item.toDate)}</td>
                                                <td>{item.costCenter}</td>
                                            </tr>
                                        ))}
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="form-controls-container">
                        {showAddRow ? (
                            <>
                                <LoadingButton
                                    className="form-btn btn-blue"
                                    onClick={handleSave}
                                    loading={saving}
                                    disabled={!fromDate || !costCenter}
                                >
                                    {t('common.save')}
                                </LoadingButton>
                                <button
                                    type="button"
                                    className="form-btn btn-red"
                                    onClick={() => {
                                        setShowAddRow(false);
                                        setFromDate('');
                                        setToDate('');
                                        setCostCenter('');
                                    }}
                                >
                                    {t('common.cancel')}
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                className="form-btn btn-red"
                                onClick={() => onClose(false)}
                            >
                                {t('common.cancel')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}